import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Informe seu email."); return; }
    setLoading(true);
    setError("");
    try {
      const resp = await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
      if (resp.data?.reset_link) setResetLink(resp.data.reset_link);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || "Falha ao processar solicitação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground leading-tight" style={{ fontFamily: "Manrope" }}>
              RedeCicatriza
            </h1>
            <p className="text-xs text-muted-foreground">Atenção Primária</p>
          </div>
        </div>

        {sent ? (
          <div className="text-center space-y-4 animate-fade-in">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
              Link gerado!
            </h2>
            <p className="text-sm text-muted-foreground">
              Clique no link abaixo para redefinir sua senha. Este link expira em 1 hora.
            </p>
            {resetLink && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-left">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Seu link de recuperação:</p>
                <a
                  href={resetLink}
                  className="text-primary text-sm break-all hover:underline font-medium"
                  data-testid="reset-link"
                >
                  {resetLink}
                </a>
              </div>
            )}
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 w-full py-3 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all mt-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao login
            </Link>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-foreground mb-1" style={{ fontFamily: "Manrope" }}>
              Recuperar senha
            </h2>
            <p className="text-muted-foreground mb-6 text-sm">
              Informe seu email cadastrado para receber o link de recuperação.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(""); }}
                    placeholder="seu@email.com"
                    autoComplete="email"
                    className="w-full h-12 border border-border rounded-lg pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="forgot-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all disabled:opacity-60"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {loading ? "Gerando link..." : "Enviar link"}
              </button>
            </form>

            <Link
              to="/login"
              className="flex items-center justify-center gap-2 w-full py-3 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all mt-4"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
